import { useMutation, useQuery } from '@tanstack/react-query';
import { Plus, Receipt, Users, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, Select } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Ref, TicketMeta } from '@/lib/types';

/** Beteiligte eines Tickets: sehen es immer und werden über Nachrichten und Statuswechsel informiert. */
export function WatchersCard({ ticketId, watchers, billingWatching, canUpdate, onChange }: { ticketId: string; watchers: Ref[]; billingWatching: boolean; canUpdate: boolean; onChange: () => void }) {
  const { me } = useAuth();
  const [pick, setPick] = useState('');
  const { data: meta } = useQuery({ queryKey: ['tickets', 'meta'], queryFn: () => api<TicketMeta>('/tickets/meta'), enabled: canUpdate });
  const onError = (e: unknown) => toast.error(errorMessage(e));
  const add = useMutation({
    mutationFn: (body: { userIds?: string[]; billing?: boolean }) => api(`/tickets/${ticketId}/watchers`, { method: 'POST', body }),
    onSuccess: (_, body) => {
      if (body.billing) toast.success('Buchhaltung hinzugezogen – sie wird über Antworten informiert');
      setPick('');
      onChange();
    },
    onError,
  });
  const remove = useMutation({ mutationFn: (userId: string) => api(`/tickets/${ticketId}/watchers/${userId}`, { method: 'DELETE' }), onSuccess: onChange, onError });
  const iWatch = watchers.some((w) => w.id === me?.user.id);
  const candidates = meta?.agents.filter((a) => !watchers.some((w) => w.id === a.id)) ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Users className="size-4" /> Beteiligte
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2 text-sm">
        {watchers.length === 0 && <p className="text-muted-foreground">Niemand zusätzlich beteiligt.</p>}
        {watchers.map((w) => (
          <div key={w.id} className="flex items-center justify-between gap-2">
            <span className="truncate">{w.name}</span>
            {(canUpdate || w.id === me?.user.id) && (
              <button className="text-muted-foreground hover:text-destructive" onClick={() => remove.mutate(w.id)} aria-label={`${w.name} entfernen`}>
                <X className="size-4" />
              </button>
            )}
          </div>
        ))}
        <div className="flex flex-wrap gap-2 border-t pt-2">
          {canUpdate && !billingWatching && (
            <Button size="sm" variant="outline" onClick={() => add.mutate({ billing: true })} loading={add.isPending && add.variables?.billing}>
              <Receipt /> Buchhaltung hinzuziehen
            </Button>
          )}
          {!iWatch && me && (
            <Button size="sm" variant="ghost" onClick={() => add.mutate({ userIds: [me.user.id] })}>
              <Plus /> Mich beteiligen
            </Button>
          )}
        </div>
        {canUpdate && candidates.length > 0 && (
          <div className="flex gap-2">
            <Select value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Mitarbeiter beteiligen" className="h-8 text-xs">
              <option value="">Mitarbeiter beteiligen …</option>
              {candidates.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
            <Button size="icon" variant="outline" className="size-8 shrink-0" disabled={!pick} onClick={() => add.mutate({ userIds: [pick] })} aria-label="Hinzufügen">
              <Plus />
            </Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground">Beteiligte sehen das Ticket und erhalten E-Mails bei neuen Nachrichten, Notizen und Statuswechseln.</p>
      </CardContent>
    </Card>
  );
}
